// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

// material styles
import { Grid, Button, TextField } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  addProductRequest,
  getProductsCategoryRequest,
} from "api/requests/products";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";

// form validate
import { productAddItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./addProduct.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import BackButton from "components/shared/BackButton";

export default function AddProduct() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(productAddItemSchema),
  });

  // editor value
  const [text, setText] = useState("");

  // upload image and video state
  const [productImage, setProductImage] = useState({
    url: null,
    isChanged: false,
  });
  const [productVideo, setProductVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // all categories and subcategories state
  const [allCategories, setAllCategories] = useState({ data: [], count: 0 });

  // select option state
  const [categoryList, setCategoryList] = useState({
    data: [],
    count: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");

  const [subCategoryList, setSubCategoryList] = useState({
    data: [],
    count: 0,
  });

  // uploaded image handler method
  const handleChangeProductImage = (val) => {
    validateImg(val);
    setProductImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeProductVideo = (val) =>
    setProductVideo({ url: val, isChanged: true });

  // add product
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!productImage.url) return Toast({ type: "empty", title: "فیلد عکس " });
    const res = await request({
      url: () =>
        addProductRequest(
          {
            text,
            title: data.title,
            category_id: data.category,
            subcategory_id: selectedSubCategory,
            alt: data.alt,
            meta_description: data.meta_description,
            keyword: data.keyword,
            image: productImage,
            video: productVideo,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "ثبت محصول" });
      navigate("/products");
    }
  };

  // get product categories data
  const getProductCategoriesData = useCallback(async () => {
    const categoryRes = await request({
      url: getProductsCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
        is_parent: 0, // get all categoies and subcategories.
      },
      isMulty: true,
    });
    if (categoryRes) {
      setAllCategories({
        data: categoryRes.data.data,
        count: categoryRes.data.count,
      });
      let categoriesData = categoryRes.data.data.filter(
        (item) => item.parent_id == null
      );
      setCategoryList({ data: categoriesData, count: categoriesData.length });
    }
  }, [request]);

  useEffect(() => {
    setSelectedSubCategory(null);
    let data = allCategories.data.filter(
      (item) => item.parent_id == selectedCategory
    );
    setSubCategoryList({ data: data, count: data.length });
  }, [selectedCategory]);

  useEffect(() => {
    getProductCategoriesData();
  }, [getProductCategoriesData]);

  return (
    <DrawerCM title="افزودن محصول">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/products" />
          </Grid>
          <StyledStack>
            <StyledTitle>افزودن محصول</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان محصول"
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>
              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="سردسته"
                  control={control}
                  title="سردسته"
                  fullWidth
                  id="category"
                  {...register("category")}
                  error={Boolean(errors.category)}
                  helperText={errors.category?.message}
                  onChange={(value) => setSelectedCategory(value)}
                >
                  {categoryList?.data.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="زیردسته"
                  control={control}
                  title="زیردسته"
                  id="subCategory"
                  {...register("subCategory")}
                  error={Boolean(errors.subCategory)}
                  helperText={errors.subCategory?.message}
                  onChange={(value) => setSelectedSubCategory(value)}
                >
                  {subCategoryList?.data.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {productImage.url ? (
                  <Upload
                    isWeb={productImage.isWeb}
                    isLocal={!productImage.isWeb}
                    file={productImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeProductImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={productImage.isChanged}
                    file={productImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeProductImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {productVideo.url ? (
                  <UploadVideoCM
                    isWeb={productVideo.isWeb}
                    isLocal={!productVideo.isWeb}
                    file={productVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeProductVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={productVideo.isChanged}
                    file={productVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeProductVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <Editor
                  enableToolbar={true}
                  onChange={handleChangeText}
                  defaultValue={text}
                  setDefaultStyle="font-family: IranSans; height: 40vh;"
                  setOptions={{
                    buttonList: buttonList.complex,
                    font: [
                      "IranSans",
                      "IranSansBold",
                      "Arial",
                      "Impact",
                      "Tahoma",
                    ],
                  }}
                />
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
        </Grid>
      )}
    </DrawerCM>
  );
}
